<script>
  import { createEventDispatcher } from "svelte";

  import {
    copyObjContent,
    getFormatDateAndTime,
    getText,
    goToURL,
    isObjectEmpty,
    showError,
  } from "../../../Utils/utilFunctions.svelte";

  import { CardLayout, Loader, NotificationModal } from "../index.svelte";

  import { duplicateIcon } from "../../../Utils/utilConstants.svelte";

  import { apiMethods } from "../../../Services/index.svelte";

  export let logDetails = {};

  const dispatch = createEventDispatcher();

  $: btnOptions = [
    {
      iconName: "copyIcon",
      iFont: duplicateIcon,
      hasTooltip: true,
      tooltip: "Copy content",
      iconFunction: () => copyContent(),
    },
  ];

  $: displayData = [
    {
      label: "Action Time:",
      value: getFormatDateAndTime(logDetails?.createdDate),
    },
    {
      label: "Action:",
      value: _getActionName(logDetails?.actionType),
    },
    {
      label: "URL:",
      value: getText(logDetails?.apiUrl),
    },
    {
      label: "Doctor Name:",
      value: logDetails?.accountName
        ? getText(logDetails.accountName)
        : "Unknown Doctor",
      clickFunction: logDetails?.accountName
        ? () => _redirectToAccount()
        : null,
    },
    {
      label: "Encounter ID:",
      value: logDetails?.encounterId
        ? getText(logDetails.encounterId)
        : "Unknown Patient Encounter",
      clickFunction: logDetails?.encounterId
        ? () => _redirectToEncounter()
        : null,
    },
    {
      label: "Transaction ID:",
      value: getText(logDetails?.transactionId),
    },
    {
      label: "Description:",
      value: logDetails?.description
        ? getText(logDetails.description)
        : "No description",
    },
  ];

  $: showClaimLink =
    logDetails?.description &&
    logDetails?.transactionId &&
    descriptionsShowClaimLink.some(
      (description) =>
        String(logDetails.description).toLowerCase() ==
        description.toLowerCase(),
    );

  const descriptionsShowClaimLink = [
    "InPatientMedicalClaim",
    "BulkBillStoreForward",
    "DvaMedicalClaim",
  ];

  let isClaimingStatus = false,
    isClaimingEraStatus = false,
    claimResponse = {},
    showClaimResultModal = false;

  const copyContent = () => {
    const copyObj = {};

    displayData.map((data) => (copyObj[data.label] = data.value));

    copyObjContent(copyObj, "Basic Information Copied!");
  };

  const handleRunManualStatusReport = async () => {
    if (isClaimingStatus) return;

    const { encounterId, transactionId } = logDetails;

    if (!encounterId || !transactionId) return;

    isClaimingStatus = true;

    await apiMethods.postMethod
      .claimEncounterStatus({
        patientEncounterId: encounterId,
        claimTransactionId: transactionId,
      })
      .then((res) => {
        if (!res || res.error)
          showError(
            res.error ??
              "Failed to claim, please refresh the page and try again and contact support if the issue persists.",
          );
        else _openClaimResultModal(res);
      });

    isClaimingStatus = false;
  };

  const handleRunManualPaymentReport = async () => {
    if (isClaimingEraStatus) return;

    const { encounterId, transactionId } = logDetails;

    if (!encounterId || !transactionId) return;

    isClaimingEraStatus = true;

    await apiMethods.postMethod
      .claimEncounterEraStatus({
        patientEncounterId: encounterId,
        claimTransactionId: transactionId,
      })
      .then((res) => {
        if (!res || res.error)
          showError(
            res.error ??
              "Failed to claim, please refresh the page and try again and contact support if the issue persists.",
          );
        else _openClaimResultModal(res);
      });

    isClaimingEraStatus = false;
  };

  // HELPER FUNCTIONS
  const _getActionName = (actionType) => {
    switch (actionType) {
      case 0:
        return "POST";
      case 1:
        return "GET";
      case 3:
        return "PUT";
      case 4:
        return "DELETE";
      default:
        return "";
    }
  };

  const _redirectToAccount = () => {
    goToURL(`/Account/view/${logDetails.accountId}`);
    dispatch("closeModal");
  };

  const _redirectToEncounter = () => {
    goToURL(`/PatientEncounter/view/${logDetails.encounterId}/tab=logs`);
    dispatch("closeModal");
  };

  const _openClaimResultModal = (res = {}) => {
    if (!res || isObjectEmpty(res)) return;

    showClaimResultModal = true;
    claimResponse = res;
  };

  const _closeClaimResultModal = () => {
    showClaimResultModal = false;
    claimResponse = {};
  };
</script>

<CardLayout title="Basic Information" {btnOptions}>
  <div class="flex flex-col p-2 w-full gap-y-3">
    {#each displayData as data}
      <div class="flex flex-row gap-x-2">
        <p class="title">{data.label}</p>

        {#if data.label == "Transaction ID:" && showClaimLink}
          <div class="content flex gap-x-4 items-center flex-wrap">
            <p>{data.value}</p>

            <p
              class={isClaimingStatus
                ? "cursor-not-allowed"
                : "underline cursor-pointer hover:font-bold"}
              on:click={handleRunManualStatusReport}
            >
              {#if isClaimingStatus}
                <Loader />
              {/if}

              Run Manual Status Report
            </p>

            <p
              class={isClaimingEraStatus
                ? "cursor-not-allowed"
                : "underline cursor-pointer hover:font-bold"}
              on:click={handleRunManualPaymentReport}
            >
              {#if isClaimingEraStatus}
                <Loader />
              {/if}

              Run Manual Payment Report(ERA)
            </p>
          </div>
        {:else}
          <p
            class={`content break-all ${data.clickFunction ? "cursor-pointer underline hover:font-bold" : ""}`}
            on:click|preventDefault={data.clickFunction}
          >
            {data.value}
          </p>
        {/if}
      </div>
    {/each}
  </div>
</CardLayout>

<NotificationModal
  isModalOpen={showClaimResultModal}
  title="Done"
  showCancelBtn={false}
  confirmAction={_closeClaimResultModal}
  closeModalFunction={_closeClaimResultModal}
  submitText="Close"
  modalMaxWidth="max-w-4xl"
>
  <div class="flex flex-col flex-1 overflow-hidden px-4 py-2 gap-y-2">
    <p class="font-bold">Result:</p>
    <div class="flex flex-1 overflow-x-hidden overflow-y-auto">
      <div class="flex flex-col gap-y-1 w-full max-h-[500px]">
        {#each Object.entries(claimResponse) as [key, value]}
          {#if value != null}
            <div class="flex w-full items-start gap-x-2">
              <p class="capitalize w-1/3 break-all">{getText(key)}</p>
              <p class="w-2/3">{getText(value, "-")}</p>
            </div>
          {/if}
        {/each}
      </div>
    </div>
  </div>
</NotificationModal>

<style>
  .title {
    width: 150px;
  }

  .content {
    width: calc(100% - 150px);
  }
</style>
